import { useQuery, useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import type { ApiClient } from '../api/index';

export function createResourceStore<T = unknown>(apiClient: ApiClient) {
  const baseKey = apiClient.baseUrl;

  return function useResourceHooks() {
    const qc = useQueryClient();

    function useList(query?: Record<string, string>) {
      const result = useQuery<T[]>({
        queryKey: [baseKey, '/', query],
        queryFn: () => apiClient.get<T[]>('/', { query }),
      });
      return { ...result, items: result.data ?? [], loading: result.isLoading };
    }

    function useGet(id: string | number, options: { enabled?: boolean; [key: string]: unknown } = {}) {
      const result = useQuery<T>({
        queryKey: [baseKey, id],
        queryFn: () => apiClient.get<T>(`/${id}`),
        enabled: !!id && (options.enabled !== false),
        ...options,
      });
      return { ...result, current: result.data ?? null, loading: result.isLoading };
    }

    function useCreate(mutationOptions: UseMutationOptions<T, Error, unknown> = {}) {
      return useMutation<T, Error, unknown>({
        mutationFn: (body) => apiClient.post<T>('/', { body }),
        onSuccess: () => qc.invalidateQueries({ queryKey: [baseKey] }),
        ...mutationOptions,
      });
    }

    function useUpdate(mutationOptions: UseMutationOptions<T, Error, { id: string | number; body: unknown }> = {}) {
      return useMutation<T, Error, { id: string | number; body: unknown }>({
        mutationFn: ({ id, body }) => apiClient.put<T>(`/${id}`, { body }),
        onSuccess: () => qc.invalidateQueries({ queryKey: [baseKey] }),
        ...mutationOptions,
      });
    }

    function useRemove(mutationOptions: UseMutationOptions<T, Error, string | number> = {}) {
      return useMutation<T, Error, string | number>({
        mutationFn: (id) => apiClient.delete<T>(`/${id}`),
        onSuccess: () => qc.invalidateQueries({ queryKey: [baseKey] }),
        ...mutationOptions,
      });
    }

    return { useList, useGet, useCreate, useUpdate, useRemove };
  };
}
